import React, { useEffect, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { COSMOS_URL } from 'components/globals';
import { useObservability } from 'helpers/observability';

import type { MenuConfig, NavigationProps } from '../../types';
import { MobileMenuAccountItemsV2 } from '../mobile/MobileMenuAccountItems';
import { MobileMenuList } from '../mobile/MobileMenuList';
import { MobileMenuSubMenu } from '../mobile/MobileMenuSubMenu';
import { MenuCTA } from './MenuCTA';

import styles from './MenuContent.module.css';

interface MenuContentProps extends NavigationProps, MenuConfig {
  isVisible: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onAnimationComplete: (definition: 'visible' | 'hidden') => void;
  variant?: 'default' | 'glass';
}

export const MenuContent: React.FC<MenuContentProps> = ({
  isVisible,
  containerRef,
  onAnimationComplete,
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  megaNavData,
  variant = 'default',
}) => {
  const { authentication, logout } = useAuth();
  // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
  const { trackEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';

  const accountItems: React.ReactNode = isLoggedIn ? (
    <MobileMenuAccountItemsV2 slug={slug} />
  ) : null;

  const handleSignOut = () => {
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  // Keep the most recently opened submenu mounted so it can slide back out.
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  useEffect(() => {
    if (openSubMenu) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setActiveSubMenu(openSubMenu);
    }
  }, [openSubMenu]);

  // Slide the panel in on the frame after mount; slide out is driven by `isVisible`.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  const open = isVisible && entered;
  const showSubMenu = Boolean(megaNavData && openSubMenu);

  const handleTransitionEnd = (event: React.TransitionEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }
    onAnimationComplete(open ? 'visible' : 'hidden');
  };

  const handleSignIn = () => {
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
    document.location.href = `${COSMOS_URL}/login?redirectTo=${encodeURIComponent(
      window.location.pathname,
    )}`;
  };

  const signInContent =
    authentication.state === 'not_authenticated' ? (
      <Col>
        <Box
          onClick={handleSignIn}
          aria-label="Sign in to your account"
          analyticsName="cosmos-mobile-menu-sign-in"
          className="inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-xl border-0 bg-white/90 px-6 font-medium text-od-content-primary shadow-[0_0_0_1px_rgb(88_64_50_/_0.16),inset_0_0_0_1px_#fff,inset_0_-4px_0_0_rgb(88_64_50_/_0.1)]"
        >
          <Text as="span" className="text-[16px] font-medium tracking-[-0.8px] text-inherit">
            Sign in
          </Text>
        </Box>
      </Col>
    ) : null;

  const signOutContent = isLoggedIn ? (
    <Row className="justify-center px-3 py-4">
      <Box
        onClick={handleSignOut}
        aria-label="Sign out"
        analyticsName="cosmos-account-menu-sign-out"
        className="inline-flex items-center"
      >
        <Text as="span" className="text-body-md text-od-content-tertiary">
          Sign out
        </Text>
      </Box>
    </Row>
  ) : null;

  const menuFooter = (
    <Col className="px-5 pt-5 pb-5">
      {signInContent}
      {signOutContent}
      <MenuCTA ctaLink={ctaLink} ctaTitle={ctaTitle} open={open} />
    </Col>
  );

  const mainMenu = (
    <MobileMenuList
      megaNavData={megaNavData}
      menuPanelNavLinks={menuPanelNavLinks}
      mainNavLinks={mainNavLinks}
      slug={slug}
      onSubMenuOpen={setOpenSubMenu}
      accountItems={accountItems}
      footer={menuFooter}
    />
  );

  return (
    <Box
      ref={containerRef}
      tabIndex={-1}
      className={cn(
        styles.menuContainer,
        'grid transition-transform duration-500 ease-out',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
      data-variant={variant}
      role="dialog"
      aria-labelledby="dialog-main-menu"
      aria-modal="true"
      onTransitionEnd={handleTransitionEnd}
    >
      {/* eslint-disable-next-line @opendoor/no-html-elements -- Atoms has no list primitive; <ul> preserves list semantics */}
      <ul className={cn(styles.menuList, 'relative block')}>
        {megaNavData ? (
          <>
            <Col
              className={cn(
                'transition-transform duration-300 ease-in-out',
                showSubMenu ? 'absolute inset-0 -translate-x-full' : 'relative translate-x-0',
              )}
            >
              {mainMenu}
            </Col>
            {activeSubMenu && (
              <Col
                className={cn(
                  'transition-transform duration-300 ease-in-out',
                  showSubMenu ? 'relative translate-x-0' : 'absolute inset-0 translate-x-full',
                )}
              >
                <MobileMenuSubMenu
                  megaNavData={megaNavData}
                  openSubMenu={activeSubMenu}
                  onBack={() => setOpenSubMenu(null)}
                  footer={menuFooter}
                />
              </Col>
            )}
          </>
        ) : (
          mainMenu
        )}
      </ul>
    </Box>
  );
};
